import React, { useEffect, useRef, useState } from "react";
import { Descriptions, Divider, Spin, Statistic, Tag, Typography } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import useDebounce from "../../hooks/useDebounce";
import { getEntityPreview } from "../../services/search/search.service";
import {
  EntityPreview,
  PreviewHierarchyItem,
  PreviewStatus,
  SearchEntity,
} from "../../services/search/search.types";
import {
  ENTITY_GROUP_LABEL,
  ENTITY_TAG_COLOR,
  getHierarchyPath,
  getNavigationPath,
} from "./utils";
import LinkButton from "../LinkButton";

// Same relative-time setup used by the LastModified component so both surfaces
// read identically ("Última modificación hace 2 días").
dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;

interface PreviewPanelProps {
  entity: SearchEntity | null;
  id: number | null;
  // Called when the "Abrir" CTA triggers an in-app navigation, so the host
  // palette can close itself (modified clicks that open a new tab skip it).
  onNavigate?: () => void;
}

// Maps the backend semantic tone to an antd Tag preset color (no hardcoded
// hex — the presets already adapt to light/dark themes).
const TONE_TAG_COLOR: Record<PreviewStatus["tone"], string> = {
  active: "success",
  warn: "warning",
  neutral: "default",
};

// Builds the cache/debounce key for an (entity, id) pair. Returns null when
// there is no active item so the panel can fall back to its placeholder.
const buildKey = (entity: SearchEntity | null, id: number | null): string | null =>
  entity != null && id != null ? `${entity}-${id}` : null;

// Ancestor breadcrumb ("Programa X › Curso Y"). Each ancestor links to its
// detail route when the entity is routable; otherwise it stays plain text so
// we never invent a broken path. Single line with graceful end-ellipsis.
const HierarchyBreadcrumb: React.FC<{ items: PreviewHierarchyItem[] }> = ({
  items,
}) => (
  <div
    data-testid="preview-hierarchy"
    title={items.map((item) => item.title).join(" › ")}
    style={{
      display: "block",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      marginBottom: 8,
      fontSize: 12,
    }}
  >
    {items.map((item, index) => {
      const path = getHierarchyPath(item.entity, item.id);
      return (
        <React.Fragment key={`${item.entity}-${item.id}-${index}`}>
          {index > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {" › "}
            </Text>
          )}
          {path ? (
            <Link to={path} style={{ fontSize: 12 }}>
              {item.title}
            </Link>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {item.title}
            </Text>
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// Bounded resource thumbnail. A broken URL simply removes itself (onError)
// rather than showing a broken-image glyph.
const PreviewThumbnail: React.FC<{ src: string; alt: string }> = ({
  src,
  alt,
}) => {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return (
    <img
      data-testid="preview-thumbnail"
      src={src}
      alt={alt}
      onError={() => setBroken(true)}
      style={{
        display: "block",
        width: "100%",
        maxHeight: 140,
        objectFit: "cover",
        borderRadius: 8,
        marginBottom: 12,
        border: "1px solid var(--bo-card-border)",
      }}
    />
  );
};

const PreviewPanel: React.FC<PreviewPanelProps> = ({ entity, id, onNavigate }) => {
  const key = buildKey(entity, id);
  // ~200ms so quickly arrowing/hovering across results doesn't fire a request
  // per item; only the item the cursor settles on is fetched.
  const debouncedKey = useDebounce(key, 200);

  const [preview, setPreview] = useState<EntityPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // In-memory cache keyed by `${entity}-${id}` so returning to an item already
  // seen this session doesn't re-hit the network. Lives for the palette's
  // lifetime (cleared on unmount with the component).
  const cacheRef = useRef<Map<string, EntityPreview>>(new Map());
  // "Last request wins": a slow, stale response can't clobber the preview of
  // the item the cursor moved to afterwards (same pattern as index.tsx).
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    if (debouncedKey === null || entity === null || id === null) {
      latestRequestIdRef.current += 1; // invalidate any in-flight request
      setPreview(null);
      setLoading(false);
      setError(false);
      return;
    }

    const cached = cacheRef.current.get(debouncedKey);
    if (cached) {
      latestRequestIdRef.current += 1; // ignore any in-flight request
      setPreview(cached);
      setLoading(false);
      setError(false);
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    setLoading(true);
    setError(false);
    getEntityPreview(entity, id)
      .then((data) => {
        if (requestId !== latestRequestIdRef.current) return;
        cacheRef.current.set(debouncedKey, data);
        setPreview(data);
      })
      .catch(() => {
        if (requestId !== latestRequestIdRef.current) return;
        setPreview(null);
        setError(true);
      })
      .finally(() => {
        if (requestId === latestRequestIdRef.current) setLoading(false);
      });
    // `entity`/`id` are the source of `debouncedKey`; keying the effect on the
    // debounced value keeps them consistent (no newer change for 200ms).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey]);

  if (key === null) {
    return (
      <div style={{ padding: 8 }}>
        <Text type="secondary">
          Pasa el cursor sobre un resultado para ver su detalle.
        </Text>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spin size="small" />
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div style={{ padding: 8 }}>
        <Text type="secondary">No se pudo cargar el preview.</Text>
      </div>
    );
  }

  const openPath = getNavigationPath(preview.entity, preview.id);

  return (
    <div style={{ padding: 8 }}>
      {/* Header: entity type badge, id and status, plus a real-anchor CTA. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <Tag color={ENTITY_TAG_COLOR[preview.entity]} style={{ margin: 0 }}>
          {ENTITY_GROUP_LABEL[preview.entity] ?? preview.entity}
        </Tag>
        <Text type="secondary">#{preview.id}</Text>
        {preview.status && (
          <Tag color={TONE_TAG_COLOR[preview.status.tone]} style={{ margin: 0 }}>
            {preview.status.label}
          </Tag>
        )}
      </div>

      {preview.hierarchy && preview.hierarchy.length > 0 && (
        <HierarchyBreadcrumb items={preview.hierarchy} />
      )}

      <Title level={5} ellipsis={{ rows: 2 }} style={{ marginBottom: 4 }}>
        {preview.title}
      </Title>

      {preview.typeLabel && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {preview.typeLabel}
        </Text>
      )}

      {preview.thumbnail_url && (
        <PreviewThumbnail src={preview.thumbnail_url} alt={preview.title} />
      )}

      {preview.fields.length > 0 && (
        <Descriptions
          size="small"
          column={1}
          colon={false}
          style={{ marginBottom: preview.counts.length > 0 ? 12 : 0 }}
        >
          {preview.fields.map((field, index) => (
            <Descriptions.Item
              key={`${field.label}-${index}`}
              label={field.label}
            >
              <Paragraph
                ellipsis={{ rows: 2 }}
                style={{ margin: 0 }}
                title={field.value}
              >
                {field.value}
              </Paragraph>
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {preview.counts.length > 0 && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {preview.counts.map((count, index) => (
            <Statistic
              key={`${count.label}-${index}`}
              title={count.label}
              value={count.value}
              valueStyle={{ fontSize: 18 }}
            />
          ))}
        </div>
      )}

      {preview.last_modified && (
        <Text
          data-testid="preview-last-modified"
          type="secondary"
          style={{ display: "block", marginTop: 12, fontSize: 12 }}
          title={dayjs(preview.last_modified.at).format("DD/MM/YYYY HH:mm")}
        >
          {`Última modificación ${dayjs(preview.last_modified.at)
            .locale("es")
            .fromNow()}${
            preview.last_modified.by ? ` por ${preview.last_modified.by}` : ""
          }`}
        </Text>
      )}

      <Divider style={{ margin: "16px 0 12px" }} />

      <LinkButton
        to={openPath}
        type="primary"
        block
        icon={<ArrowRightOutlined />}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          onNavigate?.();
        }}
      >
        Abrir
      </LinkButton>
    </div>
  );
};

export default PreviewPanel;
